import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { CurationService } from '../curation/curation.service';
import { CurationCountsService } from '../curation/curation-counts.service';
import { defaultPeriod } from '../../core/utils/period.util';

interface AdminTab {
  label: string;
  /** Rota absoluta para a qual o tab navega. */
  route: string;
  /** Prefixo de URL usado para marcar o tab como ativo. */
  matchPrefix: string;
  /** Quando true, anexa o contador de pendentes da curadoria ao label. */
  showPendingCount?: boolean;
}

/**
 * Shell (hub) da administração do Smart Review. Envolve as áreas de `smartreview.admin` —
 * Gates e Curadoria — num único po-page com po-tabs (PO-UI nativo). As abas
 * dirigem a navegação por rotas filhas renderizadas no <router-outlet>; envolver
 * tudo num po-page garante o offset correto ao lado do po-menu lateral. Novas
 * áreas (ex.: consumo de tokens, liberação de acesso) entram adicionando um item
 * em `tabs` e uma rota filha em smart-review.routes.ts.
 */
@Component({
  selector: 'app-admin-shell',
  templateUrl: './admin-shell.component.html',
  styleUrls: ['./admin-shell.component.css']
})
export class AdminShellComponent implements OnInit, OnDestroy {
  readonly tabs: AdminTab[] = [
    { label: 'Controles', route: '/menu/smart-review/admin/gates', matchPrefix: '/menu/smart-review/admin/gates' },
    { label: 'Curadoria', route: '/menu/smart-review/admin/curation/queue', matchPrefix: '/menu/smart-review/admin/curation/queue', showPendingCount: true },
    { label: 'Painel de Curadoria', route: '/menu/smart-review/admin/curation/stats', matchPrefix: '/menu/smart-review/admin/curation/stats' },
    { label: 'Custos', route: '/menu/smart-review/admin/costs', matchPrefix: '/menu/smart-review/admin/costs' },
    { label: 'Dashboard', route: '/menu/smart-review/admin/dashboard', matchPrefix: '/menu/smart-review/admin/dashboard' },
    { label: 'Repositórios', route: '/menu/smart-review/admin/repositories', matchPrefix: '/menu/smart-review/admin/repositories' }
  ];

  pendingCount?: number;

  private activeRoute = '';
  private readonly subs = new Subscription();

  constructor(
    private router: Router,
    private curationService: CurationService,
    private curationCounts: CurationCountsService
  ) {}

  ngOnInit(): void {
    this.updateActive(this.router.url);
    this.subs.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => this.updateActive(event.urlAfterRedirects))
    );
    // O badge da aba Curadoria reflete o total de pendentes, mantido ao vivo pela
    // fila (curar/descartar) via CurationCountsService.
    this.subs.add(
      this.curationCounts.pending$.subscribe(count => { this.pendingCount = count; })
    );
    // Re-sincroniza a cada entrada no hub (o serviço é singleton e pode reter um
    // valor de visita anterior).
    this.loadPendingCount();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  tabLabel(tab: AdminTab): string {
    if (tab.showPendingCount && this.pendingCount != null) {
      return `${tab.label} (${this.pendingCount})`;
    }
    return tab.label;
  }

  isActive(tab: AdminTab): boolean {
    return this.activeRoute.startsWith(tab.matchPrefix);
  }

  navigate(tab: AdminTab): void {
    if (!this.isActive(tab)) {
      this.router.navigate([tab.route]);
    }
  }

  private loadPendingCount(): void {
    // Carga inicial do badge (antes da fila ser aberta); a partir daí a fila
    // mantém o número vivo via CurationCountsService. Usa o mesmo período default
    // da fila (últimos 30 dias) para o badge bater com a aba "Pendentes".
    const period = defaultPeriod();
    this.curationService
      .getQueue({ status: 'pending', limit: '1', dateFrom: period.dateFrom, dateTo: period.dateTo })
      .subscribe({
        next: res => { this.curationCounts.setPending(res?.counts?.pending ?? res?.total ?? 0); },
        error: () => { /* badge é best-effort; silencioso em caso de erro */ }
      });
  }

  private updateActive(url: string): void {
    this.activeRoute = (url || '').split('?')[0];
  }
}
